import { useMemo, useState, useEffect } from "react";
import { motion } from "framer-motion";

import {
  Button,
  Card,
  Divider,
  Spinner,
  TextInput,
  EmptyState,
  PageHeader,
  Pagination,
} from "../../../../components/shared";
import { useMyTeams } from "../../../../lib/react-query/teams";
import { HiOutlineUserGroup, HiOutlineMagnifyingGlass } from "react-icons/hi2";
import { DashboardIcon, SearchIcon, CloseIcon } from "../../../../assets/icons";
import TeamCard from "../../../../components/Teams/TeamCard";
import CreateTeamModal from "../../../../components/Teams/CreateTeamModal";

const containerVariants = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.08,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  show: {
    opacity: 1,
    y: 0,
    transition: {
      type: "spring",
      stiffness: 300,
      damping: 24,
    },
  },
};

const TEAM_PAGE_SIZE = 12;

const TeamListPage = () => {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [createModalOpen, setCreateModalOpen] = useState(false);

  const { data: teamsData, isLoading, isError } = useMyTeams({
    page,
    pageSize: TEAM_PAGE_SIZE,
    search: debouncedSearch,
    type: typeFilter,
  });
  const teams = useMemo(
    () => (Array.isArray(teamsData) ? teamsData : teamsData?.results || []),
    [teamsData],
  );
  const totalCount = teamsData?.total_count ?? teams.length;
  const totalPages = teamsData?.total_pages ?? Math.max(1, Math.ceil(totalCount / TEAM_PAGE_SIZE));

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(search);
    }, 500);
    return () => clearTimeout(handler);
  }, [search]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, typeFilter]);

  const filteredTeams = useMemo(() => {
    let result = Array.isArray(teams) ? teams : [];

    if (typeFilter !== "all") {
      result = result.filter((team) =>
        typeFilter === "personal" ? team.is_personal : !team.is_personal,
      );
    }

    if (debouncedSearch.trim()) {
      const query = debouncedSearch.toLowerCase();
      result = result.filter(
        (team) =>
          team.name?.toLowerCase().includes(query) ||
          team.description?.toLowerCase().includes(query),
      );
    }

    return result;
  }, [teams, typeFilter, debouncedSearch]);

  const isFiltered = !!debouncedSearch || typeFilter !== "all";

  const filterButtonClass = (isActive) =>
    [
      "rounded-full border px-4 py-2 text-sm font-medium transition-colors",
      isActive
        ? "border-mediumgrey bg-surface-hover text-darkgrey"
        : "border-surface-border bg-white text-mediumgrey hover:border-mediumgrey hover:text-darkgrey",
    ].join(" ");

  return (
    <div className="min-h-full bg-bggrey">
      <div className="mx-auto max-w-6xl py-6 md:py-8">
        <Card variant="elevated" radius="xl" className="overflow-hidden">
          <div className="p-4 md:p-6">
            <PageHeader
              className="mb-0"
              breadcrumbs={[
                { label: "Home", href: "/dashboard" },
                { label: "Teams" },
              ]}
              title={
                <span className="flex items-center gap-3">
                  <span className="flex h-10 w-10 items-center justify-center rounded-2xl bg-surface-hover text-primary">
                    <DashboardIcon width={20} height={20} className="text-primary" />
                  </span>
                  Your workspaces
                </span>
              }
              description="Personal space for private work. Shared teams for collaboration."
              actions={
                <Button
                  className="w-full rounded-xl bg-primary font-medium lg:w-auto"
                  color="primary"
                  size="md"
                  onClick={() => setCreateModalOpen(true)}
                >
                  Create team
                </Button>
              }
            />
          </div>

          <Divider />

          <div className="flex flex-col gap-4 px-5 py-4 md:px-8 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                className={filterButtonClass(typeFilter === "all")}
                onClick={() => setTypeFilter("all")}
              >
                All
              </button>
              <button
                type="button"
                className={filterButtonClass(typeFilter === "personal")}
                onClick={() => setTypeFilter("personal")}
              >
                Personal
              </button>
              <button
                type="button"
                className={filterButtonClass(typeFilter === "shared")}
                onClick={() => setTypeFilter("shared")}
              >
                Shared
              </button>
            </div>

            <div className="w-full lg:w-80">
              <TextInput
                label=""
                type="text"
                name="search"
                placeholder="Search teams..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                startContent={
                  <SearchIcon width={16} height={16} className="text-darkgrey" />
                }
                endContent={
                  search ? (
                    <button
                      type="button"
                      onClick={() => setSearch("")}
                      className="rounded-full p-0.5 transition-colors hover:bg-lightgrey"
                    >
                      <CloseIcon width={14} height={14} className="text-darkgrey" />
                    </button>
                  ) : null
                }
              />
            </div>
          </div>
        </Card>

        <div className="pt-6">
          {isLoading ? (
            <div className="flex items-center justify-center rounded-3xl border border-surface-border bg-white py-20 shadow-sm">
              <Spinner size="lg" />
            </div>
          ) : isError ? (
            <div className="mx-auto max-w-xl rounded-2xl border border-danger/30 bg-danger/5 p-6 text-sm text-danger">
              Could not load teams. Try again in a moment, or check your session
              and API connection.
            </div>
          ) : filteredTeams.length === 0 ? (
            <EmptyState
              icon={isFiltered ? HiOutlineMagnifyingGlass : HiOutlineUserGroup}
              title={isFiltered ? "No teams found" : "No teams yet"}
              description={
                isFiltered
                  ? "Try a different search term or filter."
                  : "Every account starts with a personal workspace. Create a shared team when you want to invite collaborators."
              }
              actionText="Create team"
              onAction={() => setCreateModalOpen(true)}
            />
          ) : (
            <>
              <motion.div
                variants={containerVariants}
                initial="hidden"
                animate="show"
                className="grid grid-cols-1 gap-5 sm:grid-cols-2 xl:grid-cols-3"
              >
                {filteredTeams.map((team) => (
                  <TeamCard key={team.id} team={team} variants={itemVariants} />
                ))}
              </motion.div>
              <Pagination
                page={page}
                totalPages={totalPages}
                totalItems={totalCount}
                pageSize={TEAM_PAGE_SIZE}
                onPageChange={setPage}
              />
            </>
          )}
        </div>
      </div>

      <CreateTeamModal
        isOpen={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
      />
    </div>
  );
};

export default TeamListPage;
